import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Sparkles, Check, Loader2, AlertCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STYLES = [
    { key: "modern", label: "Modern" },
    { key: "wedding", label: "Wedding" },
    { key: "traditional", label: "Traditional" },
    { key: "casual", label: "Casual" },
];
const SKINS = [
    { key: "light", label: "Light" },
    { key: "medium", label: "Medium" },
    { key: "dark", label: "Dark" },
];

/**
 * Magic Catalog — one-shot AI on-model photo generator.
 *
 * Latency contract:
 *   - With BYOK key & cache miss: typically 10-18 seconds (Gemini Nano Banana).
 *   - Cache hit (same source+style+skin+angle): instant.
 * Frontend polls /admin/ai-jobs/{id} every 1.5 s for snappy feedback and
 * renders the actual generated image inline (no placeholder checkmark).
 */
export default function MagicCatalogDialog({ saree, open, onOpenChange, onDone }) {
    const [style, setStyle] = useState("modern");
    const [skin, setSkin] = useState("medium");
    const [busy, setBusy] = useState(false);
    const [job, setJob] = useState(null); // {job_id, status, provider, image?, error?}
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (!open) { setJob(null); setBusy(false); setElapsed(0); }
    }, [open]);

    // tick the live-elapsed counter so the user knows we're still working
    useEffect(() => {
        if (job?.status !== "running") return;
        const i = setInterval(() => setElapsed((e) => e + 1), 1000);
        return () => clearInterval(i);
    }, [job?.status]);

    const launch = async () => {
        if (!saree?.images?.length) {
            toast.error("Add at least one source photo before generating.");
            return;
        }
        setBusy(true);
        setElapsed(0);
        try {
            const { data } = await api.post(`/admin/sarees/${saree.id}/magic-catalog`, { style, skin_tone: skin });
            const j = data.jobs[0];
            setJob({ ...j, status: "running" });

            const start = Date.now();
            while (Date.now() - start < 180000) {     // 3 min hard cap
                // 1.5s poll — snappy on cache hits + fast networks.
                await new Promise((r) => setTimeout(r, 1500));
                try {
                    const { data: status } = await api.get(`/admin/ai-jobs/${j.job_id}`);
                    setJob({ ...j, ...status });
                    if (status.status === "done") {
                        toast.success(status.provider === "cache"
                            ? "Catalog photo added (from cache · ₹0)"
                            : "Catalog photo added");
                        onDone?.();
                        return;
                    }
                    if (status.status === "failed") {
                        toast.error(status.error || "Generation failed");
                        return;
                    }
                } catch { /* keep polling */ }
            }
            toast.warning("Still generating in background — refresh in a minute to see the image.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Magic catalog failed");
        } finally {
            setBusy(false);
        }
    };

    const sourceImg = saree?.images?.[0];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl tracking-editorial flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-brand-bronze" strokeWidth={1.4} />
                        Magic Catalog
                    </DialogTitle>
                </DialogHeader>

                <p className="text-brand-muted text-sm mt-1 leading-relaxed">
                    One source photo → one boutique-grade catalog photo of a model wearing this saree.
                    Each unique style is cached forever — re-runs are free.
                </p>

                {!job ? (
                    <div className="mt-6 space-y-5" data-testid="magic-options">
                        <div>
                            <div className="ayraa-overline">Style</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {STYLES.map((s) => (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => setStyle(s.key)}
                                        className={`text-xs font-mono uppercase tracking-wider-2 px-3 py-1.5 rounded-sm border transition ${style === s.key ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-border text-brand-ink hover:border-brand-bronze"}`}
                                        data-testid={`magic-style-${s.key}`}
                                    >{s.label}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="ayraa-overline">Skin tone</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {SKINS.map((s) => (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => setSkin(s.key)}
                                        className={`text-xs font-mono uppercase tracking-wider-2 px-3 py-1.5 rounded-sm border transition ${skin === s.key ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-border text-brand-ink hover:border-brand-bronze"}`}
                                        data-testid={`magic-skin-${s.key}`}
                                    >{s.label}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 flex justify-center" data-testid="magic-progress">
                        <div className="border border-brand-border bg-white p-4 flex flex-col items-center text-center w-full rounded-sm">
                            <div className="aspect-[3/4] w-full max-w-[260px] rounded-sm overflow-hidden bg-brand-subtle relative">
                                {/* Show generated image as soon as we have it.
                                    Falls back to a blurred preview of the source while we wait. */}
                                {job.status === "done" && job.image ? (
                                    <img
                                        src={job.image}
                                        alt="Generated catalog"
                                        className="absolute inset-0 w-full h-full object-cover animate-image-reveal"
                                        data-testid="magic-result-img"
                                    />
                                ) : job.status === "running" && sourceImg ? (
                                    <img
                                        src={sourceImg}
                                        alt="Source"
                                        className="absolute inset-0 w-full h-full object-cover opacity-40 blur-md"
                                    />
                                ) : null}
                                {job.status === "running" ? (
                                    <div className="absolute inset-0 grid place-items-center bg-black/30 text-white">
                                        <div className="text-center">
                                            <Loader2 className="w-7 h-7 mx-auto animate-spin" strokeWidth={1.5} />
                                            <div className="font-mono text-[10px] uppercase tracking-wider-2 mt-2">
                                                Generating · {elapsed}s
                                            </div>
                                        </div>
                                    </div>
                                ) : job.status === "failed" ? (
                                    <div className="absolute inset-0 grid place-items-center text-red-600 p-3">
                                        <AlertCircle className="w-7 h-7" strokeWidth={1.5} />
                                    </div>
                                ) : job.status === "done" && !job.image ? (
                                    <div className="absolute inset-0 grid place-items-center text-emerald-700">
                                        <Check className="w-9 h-9" strokeWidth={1.5} />
                                    </div>
                                ) : null}
                            </div>
                            <div className="mt-4 ayraa-overline">Catalog photo</div>
                            <div className="text-sm text-brand-muted mt-1">
                                {job.status === "running" ? "Generating… ~10-20 s" :
                                    job.status === "failed" ? (job.error || "Failed").slice(0, 80) :
                                        job.provider === "cache" ? "From cache · ₹0" :
                                            job.provider === "google-direct" ? "Generated · your Gemini key" :
                                                "Generated"}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                    <div className="flex gap-2">
                        <button className="ayraa-btn-outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</button>
                        {!job ? (
                            <button className="ayraa-btn-primary" onClick={launch} disabled={busy} data-testid="magic-launch-btn">
                                <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {busy ? "Working…" : "Generate photo"}
                            </button>
                        ) : (
                            <button className="ayraa-btn-primary" onClick={() => { setJob(null); setElapsed(0); }} disabled={busy} data-testid="magic-redo-btn">
                                Try another style
                            </button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
